/**
 * Dice Registry Service - API client for dice registration endpoints
 * Handles all HTTP communication with the dice registry backend
 */

import {
  DiceType,
  DiceRegistration,
  StartRegistrationResponse,
  CompleteRegistrationResponse,
  GetRegistrationResponse,
  ImageUploadUrlResponse,
  ListImagesResponse
} from '@dice-capture/shared/types/dice-registry';
import { cognitoService } from './cognitoService';
import { createScopedReporter } from './errorReporter';
import { API_BASE_URL, API_KEY } from '@/config/api';
import { blobToBase64 } from '@/utils/blob';

const SERVICE_TAG = '[DICE_REG_API]';
const reporter = createScopedReporter('DICE_REGISTRY');

/**
 * Make authenticated request to the API.
 *
 * Includes JWT Authorization header when an authenticated
 * Cognito session is available.
 */
async function apiRequest<T>(
  endpoint: string,
  options: RequestInit = {}
): Promise<T> {
  const url = `${API_BASE_URL}${endpoint}`;

  const headers: Record<string, string> = {
    'Content-Type': 'application/json',
    'X-Api-Key': API_KEY
  };

  const idToken = await cognitoService.getIdToken();
  if (idToken) {
    headers['Authorization'] = `Bearer ${idToken}`;
  }

  const response = await fetch(url, {
    ...options,
    headers: {
      ...headers,
      ...(options.headers as Record<string, string>)
    }
  });

  if (!response.ok) {
    const errorBody = await response.json().catch(() => ({ message: 'Unknown error' }));
    reporter.error('Bone Archive API error', {
      technicalDetails: `${endpoint} returned ${response.status}`,
      context: { endpoint, status: response.status },
      showToast: false
    });
    throw new Error(errorBody.error?.message || errorBody.message || `API error: ${response.status}`);
  }

  const data = await response.json();
  return data.data || data;
}

/**
 * Dice Registry API Service
 */
export const diceRegistryService = {
  /**
   * Start a new dice registration
   * Returns presigned URLs for uploading face images
   *
   * @param deviceId - Device identifier
   * @param diceType - Type of dice to register
   * @param diceName - Optional user-friendly name
   * @returns Registration ID and upload URLs
   */
  async startRegistration(
    deviceId: string,
    diceType: DiceType,
    diceName?: string
  ): Promise<StartRegistrationResponse> {
    console.info(`${SERVICE_TAG} Starting registration`, {
      deviceId,
      diceType,
      diceName
    });

    return apiRequest<StartRegistrationResponse>('/dice-registry/start', {
      method: 'POST',
      body: JSON.stringify({ deviceId, diceType, diceName })
    });
  },

  /**
   * Upload a face image to a presigned S3 URL
   *
   * @param presignedUrl - S3 presigned PUT URL
   * @param imageBlob - Image data as Blob
   */
  async uploadFace(presignedUrl: string, imageBlob: Blob): Promise<void> {
    // Parse URL to extract useful debug info
    let urlHost = 'unknown';
    let urlPath = 'unknown';
    try {
      const parsedUrl = new URL(presignedUrl);
      urlHost = parsedUrl.host;
      urlPath = parsedUrl.pathname;
    } catch {
      reporter.warning('Could not parse presigned URL', { showToast: false });
    }

    console.info(`${SERVICE_TAG} Uploading face image`, {
      urlHost,
      urlPath,
      urlLength: presignedUrl.length,
      blobSize: imageBlob.size,
      blobType: imageBlob.type
    });

    try {
      const response = await fetch(presignedUrl, {
        method: 'PUT',
        body: imageBlob,
        headers: {
          'Content-Type': 'image/jpeg'
        }
      });

      console.info(`${SERVICE_TAG} S3 upload response`, {
        status: response.status,
        statusText: response.statusText,
        ok: response.ok
      });

      if (!response.ok) {
        // Try to get error details from response body
        let errorBody = '';
        try {
          errorBody = await response.text();
        } catch {
          errorBody = 'Could not read response body';
        }
        reporter.error('Face image upload failed', {
          technicalDetails: `S3 returned ${response.status}: ${response.statusText}`,
          context: { status: response.status, errorBody: errorBody.substring(0, 200) }
        });
        throw new Error(`Upload failed: ${response.status} - ${response.statusText}`);
      }

      console.info(`${SERVICE_TAG} Face uploaded successfully to S3`);
    } catch (error) {
      reporter.error('Face upload exception', {
        originalError: error instanceof Error ? error : undefined,
        showToast: false
      });
      throw error;
    }
  },

  /**
   * Complete a registration and trigger training
   *
   * @param registrationId - Registration identifier
   * @returns Completion status
   */
  async completeRegistration(
    registrationId: string
  ): Promise<CompleteRegistrationResponse> {
    console.info(`${SERVICE_TAG} Completing registration`, { registrationId });

    return apiRequest<CompleteRegistrationResponse>(
      `/dice-registry/registrations/${registrationId}/complete`,
      { method: 'POST' }
    );
  },

  /**
   * Get all registrations for a device
   *
   * @param deviceId - Device identifier
   * @returns Array of registrations
   */
  async getRegistrations(deviceId: string): Promise<DiceRegistration[]> {
    console.info(`${SERVICE_TAG} Fetching registrations`, { deviceId });

    const response = await apiRequest<{ registrations: DiceRegistration[] }>(
      `/dice-registry/devices/${deviceId}`
    );

    return response.registrations;
  },

  /**
   * Get a specific registration with face details
   *
   * @param deviceId - Device identifier
   * @param diceType - Dice type
   * @returns Registration with faces
   */
  async getRegistration(
    deviceId: string,
    diceType: DiceType
  ): Promise<GetRegistrationResponse> {
    console.info(`${SERVICE_TAG} Fetching registration`, { deviceId, diceType });

    return apiRequest<GetRegistrationResponse>(
      `/dice-registry/devices/${deviceId}/${diceType}`
    );
  },

  /**
   * Delete a registration
   *
   * @param deviceId - Device identifier
   * @param diceType - Dice type
   */
  async deleteRegistration(deviceId: string, diceType: DiceType): Promise<void> {
    console.info(`${SERVICE_TAG} Deleting registration`, { deviceId, diceType });

    await apiRequest(`/dice-registry/devices/${deviceId}/${diceType}`, {
      method: 'DELETE'
    });
  },

  /**
   * Get a presigned URL for uploading a specific image (TICKET-087)
   * Each image gets a unique S3 key to prevent overwrites
   *
   * @param registrationId - Registration identifier
   * @param faceNumber - Face number (1-based)
   * @param imageIndex - Image index for this face (1-based)
   * @returns Presigned URL and unique S3 key
   */
  async getImageUploadUrl(
    registrationId: string,
    faceNumber: number,
    imageIndex: number
  ): Promise<ImageUploadUrlResponse> {
    console.info(`${SERVICE_TAG} Getting image upload URL`, {
      registrationId,
      faceNumber,
      imageIndex
    });

    return apiRequest<ImageUploadUrlResponse>(
      `/dice-registry/registrations/${registrationId}/faces/${faceNumber}/upload-url`,
      {
        method: 'POST',
        body: JSON.stringify({ imageIndex })
      }
    );
  },

  /**
   * Delete a single image from S3 storage
   * Fire-and-forget: logs warning on failure but does not throw
   *
   * @param registrationId - Registration identifier
   * @param s3Key - S3 key of the image to delete
   * @returns true if deleted successfully, false on failure
   */
  async deleteImage(registrationId: string, s3Key: string): Promise<boolean> {
    console.info(`${SERVICE_TAG} Deleting image from S3`, { registrationId, s3Key });

    try {
      await apiRequest(`/dice-registry/registrations/${registrationId}/images`, {
        method: 'DELETE',
        body: JSON.stringify({ s3Key })
      });
      console.info(`${SERVICE_TAG} Image deleted from S3`, { registrationId, s3Key });
      return true;
    } catch (error) {
      reporter.warning('Failed to delete S3 image (non-blocking)', {
        originalError: error instanceof Error ? error : undefined,
        context: { registrationId, s3Key },
        showToast: false
      });
      return false;
    }
  },

  /**
   * List all images for a registration with presigned download URLs (TICKET-088)
   * Used to populate edit mode with existing captured images
   *
   * @param registrationId - Registration identifier
   * @returns List of images with download URLs
   */
  async listImages(registrationId: string): Promise<ListImagesResponse> {
    console.info(`${SERVICE_TAG} Listing images`, { registrationId });

    return apiRequest<ListImagesResponse>(
      `/dice-registry/registrations/${registrationId}/images`
    );
  },

  /**
   * Start a trained model
   *
   * @param registrationId - Registration identifier
   */
  async startModel(registrationId: string): Promise<{ status: string; message: string }> {
    console.info(`${SERVICE_TAG} Starting model`, { registrationId });

    return apiRequest(`/dice-registry/registrations/${registrationId}/model/start`, {
      method: 'POST'
    });
  },

  /**
   * Stop a running model
   *
   * @param registrationId - Registration identifier
   */
  async stopModel(registrationId: string): Promise<{ status: string; message: string }> {
    console.info(`${SERVICE_TAG} Stopping model`, { registrationId });

    return apiRequest(`/dice-registry/registrations/${registrationId}/model/stop`, {
      method: 'POST'
    });
  },

  /**
   * Get model status
   *
   * @param registrationId - Registration identifier
   */
  async getModelStatus(registrationId: string): Promise<{ status: string; message: string }> {
    console.info(`${SERVICE_TAG} Getting model status`, { registrationId });

    return apiRequest(`/dice-registry/registrations/${registrationId}/model/status`);
  },

  /**
   * Get all registrations for the authenticated user across all devices.
   * Falls back gracefully if the backend endpoint is not deployed yet.
   * @param deviceId - Current device ID for auto-clone of provider configs
   */
  async getMyRegistrations(deviceId: string): Promise<{
    registrations: DiceRegistration[];
    providerConfigs: Array<{
      deviceId: string;
      diceType: string;
      activeProviders: string[];
      availableProviders: Array<{ id: string; label: string; trained: boolean; trainingStatus?: string }>;
    }>;
  } | null> {
    console.info(`${SERVICE_TAG} Fetching user registrations (cross-device)`, { deviceId });

    try {
      return await apiRequest<{
        registrations: DiceRegistration[];
        providerConfigs: Array<{
          deviceId: string;
          diceType: string;
          activeProviders: string[];
          availableProviders: Array<{ id: string; label: string; trained: boolean; trainingStatus?: string }>;
        }>;
      }>(`/dice-registry/my-registrations?deviceId=${encodeURIComponent(deviceId)}`);
    } catch (error) {
      console.warn(`${SERVICE_TAG} my-registrations endpoint not available, falling back to device-only lookup`, {
        error: error instanceof Error ? error.message : 'Unknown error'
      });
      return null;
    }
  },

  /**
   * Upload a captured dice image to S3 and get a presigned GET URL for Discord embeds.
   * Used by the WebSocket roll result path to attach images without exceeding the
   * 128KB API Gateway WebSocket frame limit.
   *
   * @param requestId - Roll request ID (used as S3 key)
   * @param imageData - Base64-encoded JPEG (with or without data-url prefix)
   * @returns Presigned S3 GET URL (24h TTL), or null if upload fails
   */
  async uploadRollImage(requestId: string, imageData: string): Promise<string | null> {
    console.info(`${SERVICE_TAG} Uploading roll image`, { requestId });
    try {
      const result = await apiRequest<{ imageUrl: string }>('/dice/upload-image', {
        method: 'POST',
        body: JSON.stringify({ requestId, imageData })
      });
      console.info(`${SERVICE_TAG} Roll image uploaded`, { requestId });
      return result.imageUrl;
    } catch (error) {
      console.warn(`${SERVICE_TAG} Roll image upload failed (non-blocking):`, {
        error: error instanceof Error ? error.message : 'Unknown error',
        requestId
      });
      return null;
    }
  },

  /**
   * Upload a captured roll video to S3 and get a presigned GET URL (TICKET-281c).
   * Mirrors the contract of `uploadRollImage`: non-blocking, returns null on failure.
   *
   * @param requestId - Roll request ID (used as S3 key)
   * @param videoBlob - Recorded video blob (webm or mp4)
   * @returns Presigned S3 GET URL (24h TTL), or null if upload fails
   */
  async uploadRollVideo(requestId: string, videoBlob: Blob): Promise<string | null> {
    console.info(`${SERVICE_TAG} Uploading roll video`, {
      requestId,
      bytes: videoBlob.size,
      type: videoBlob.type
    });
    try {
      const videoData = await blobToBase64(videoBlob);
      const contentType: 'video/webm' | 'video/mp4' =
        videoBlob.type.startsWith('video/mp4') ? 'video/mp4' : 'video/webm';
      const result = await apiRequest<{ videoUrl: string }>('/dice/upload-video', {
        method: 'POST',
        body: JSON.stringify({ requestId, videoData, contentType })
      });
      console.info(`${SERVICE_TAG} Roll video uploaded`, { requestId });
      return result.videoUrl;
    } catch (error) {
      console.warn(`${SERVICE_TAG} Roll video upload failed (non-blocking):`, {
        error: error instanceof Error ? error.message : 'Unknown error',
        requestId
      });
      return null;
    }
  }
};
